/**
 * Scroll Restoration & History Coordinator for PSSASMM Web App
 * 
 * Rules:
 * 1. When opening another page (forward navigation, clicking links, pushState),
 *    scroll position resets to top (0, 0), NOT staying at the previous page's scroll coordinate.
 * 2. When clicking 'Kembali' or browser Back/Forward (popstate or navigateBack),
 *    scroll position restores to the old coordinate where the user was previously scrolled.
 * 3. Supports sub-view transitions (e.g. List <-> Detail) with dedicated saveViewScroll / restoreViewScroll.
 */

interface ScrollCoord {
  x: number;
  y: number;
}

const STORAGE_KEY = 'asmm_scroll_restoration_v2';

// In-memory maps
const entryScrollMap = new Map<string, ScrollCoord>();
const pathScrollMap = new Map<string, ScrollCoord>();
const viewScrollMap = new Map<string, ScrollCoord>();

let isInitialized = false;
let lastPushTimestamp = 0;
let currentScrollX = 0;
let currentScrollY = 0;

// Load persisted scroll cache from sessionStorage if available
function loadPersistedState() {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (Array.isArray(data.paths)) {
        data.paths.forEach(([p, c]: [string, ScrollCoord]) => pathScrollMap.set(p, c));
      }
      if (Array.isArray(data.entries)) {
        data.entries.forEach(([k, c]: [string, ScrollCoord]) => entryScrollMap.set(k, c));
      }
      if (Array.isArray(data.views)) {
        data.views.forEach(([v, c]: [string, ScrollCoord]) => viewScrollMap.set(v, c));
      }
    }
  } catch {
    // Ignore storage errors
  }
}

// Persist scroll cache to sessionStorage (debounced)
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
function persistState() {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    try {
      const data = {
        paths: Array.from(pathScrollMap.entries()).slice(-100),
        entries: Array.from(entryScrollMap.entries()).slice(-100),
        views: Array.from(viewScrollMap.entries()).slice(-50),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage quota
    }
  }, 200);
}

function generateKey(): string {
  return 'asmm_' + Math.random().toString(36).slice(2, 9) + '_' + Date.now().toString(36);
}

// Capture current scroll position immediately
export function recordCurrentPosition() {
  if (typeof window === 'undefined') return;
  currentScrollX = window.scrollX || window.pageXOffset || 0;
  currentScrollY = window.scrollY || window.pageYOffset || 0;

  const currentPath = window.location.pathname + window.location.search;
  const currentKey = window.history.state?.__asmm_key;

  if (currentKey) {
    entryScrollMap.set(currentKey, { x: currentScrollX, y: currentScrollY });
  }
  pathScrollMap.set(currentPath, { x: currentScrollX, y: currentScrollY });
  persistState();
}

// Reset page scroll position to top
export function resetScrollToTop() {
  if (typeof window === 'undefined') return;
  const doScroll = () => {
    window.scrollTo({ left: 0, top: 0, behavior: 'instant' as ScrollBehavior });
  };
  doScroll();
  requestAnimationFrame(doScroll);
  setTimeout(doScroll, 25);
  setTimeout(doScroll, 80);
}

// Restore saved scroll position for a given key or path, with retry for dynamic content
export function restoreScrollFor(key?: string, path?: string) {
  if (typeof window === 'undefined') return;

  let coord: ScrollCoord | undefined;
  if (key) {
    coord = entryScrollMap.get(key);
  }
  if (!coord && path) {
    coord = pathScrollMap.get(path);
    if (!coord) {
      const cleanPath = path.split('?')[0];
      coord = pathScrollMap.get(cleanPath);
    }
  }

  // If no saved coordinate, default to top (0, 0)
  const targetX = coord ? coord.x : 0;
  const targetY = coord ? coord.y : 0;

  let attempts = 0;
  const maxAttempts = 15;

  const attemptScroll = () => {
    attempts++;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
    const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const maxScrollY = Math.max(0, scrollHeight - clientHeight);

    window.scrollTo({
      left: targetX,
      top: Math.min(targetY, maxScrollY > 0 ? maxScrollY : targetY),
      behavior: 'instant' as ScrollBehavior
    });

    if (targetY > 0 && maxScrollY < targetY && attempts < maxAttempts) {
      setTimeout(attemptScroll, 35);
    }
  };

  attemptScroll();
  requestAnimationFrame(attemptScroll);
  setTimeout(attemptScroll, 40);
}

// Dedicated helper for in-app "Kembali" buttons
export function navigateBack(fallbackPath?: string) {
  recordCurrentPosition();
  const currentIdx = window.history.state?.__asmm_idx ?? 0;
  if (currentIdx > 0 && window.history.length > 1) {
    window.history.back();
    return;
  }
  if (fallbackPath) {
    window.history.pushState({ isBack: true }, '', fallbackPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

// Save view scroll position (for sub-view flows like candidate/product/campaign detail)
export function saveViewScroll(viewKey: string) {
  if (typeof window === 'undefined') return;
  const x = window.scrollX || window.pageXOffset || 0;
  const y = window.scrollY || window.pageYOffset || 0;
  viewScrollMap.set(viewKey, { x, y });
  persistState();
}

// Restore view scroll position
export function restoreViewScroll(viewKey: string) {
  if (typeof window === 'undefined') return;
  const coord = viewScrollMap.get(viewKey) || { x: 0, y: 0 };

  let attempts = 0;
  const maxAttempts = 15;
  const attemptScroll = () => {
    attempts++;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
    const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const maxScrollY = Math.max(0, scrollHeight - clientHeight);

    window.scrollTo({
      left: coord.x,
      top: Math.min(coord.y, maxScrollY > 0 ? maxScrollY : coord.y),
      behavior: 'instant' as ScrollBehavior
    });

    if (coord.y > 0 && maxScrollY < coord.y && attempts < maxAttempts) {
      setTimeout(attemptScroll, 35);
    }
  };

  attemptScroll();
  requestAnimationFrame(attemptScroll);
  setTimeout(attemptScroll, 40);
}

// Initialize the global scroll restoration engine
export function initScrollRestoration() {
  if (typeof window === 'undefined' || isInitialized) return;
  isInitialized = true;

  // 1. Tell browser not to automatically intervene with native scroll restoration
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // 2. Load stored scroll positions from sessionStorage
  loadPersistedState();

  // 3. Ensure current initial history entry has identity keys
  const currentKey = window.history.state?.__asmm_key || generateKey();
  const currentIdx = window.history.state?.__asmm_idx ?? 0;
  if (!window.history.state?.__asmm_key) {
    try {
      window.history.replaceState({
        ...(window.history.state || {}),
        __asmm_key: currentKey,
        __asmm_idx: currentIdx
      }, '');
    } catch {
      // Ignore
    }
  }

  // 4. Continuously monitor user scroll position with passive listener
  let scrollRafId: number | null = null;
  window.addEventListener('scroll', () => {
    if (scrollRafId !== null) return;
    scrollRafId = requestAnimationFrame(() => {
      scrollRafId = null;
      recordCurrentPosition();
    });
  }, { passive: true });

  // 5. Wrap history.pushState
  const originalPushState = window.history.pushState.bind(window.history);
  window.history.pushState = function (state: any, unused: string, url?: string | URL | null) {
    // Record position of the page we are leaving
    recordCurrentPosition();

    const isBack = Boolean(state && state.isBack);
    const prevIdx = window.history.state?.__asmm_idx ?? 0;
    const nextIdx = isBack ? Math.max(0, prevIdx - 1) : prevIdx + 1;
    const nextKey = isBack && state?.__asmm_key ? state.__asmm_key : generateKey();

    const enhancedState = {
      ...(state && typeof state === 'object' ? state : {}),
      __asmm_key: nextKey,
      __asmm_idx: nextIdx
    };

    lastPushTimestamp = Date.now();
    originalPushState(enhancedState, unused, url);

    const targetPath = url ? String(url) : window.location.pathname + window.location.search;

    if (isBack) {
      restoreScrollFor(nextKey, targetPath);
    } else {
      resetScrollToTop();
    }
  };

  // 6. Wrap history.replaceState
  const originalReplaceState = window.history.replaceState.bind(window.history);
  window.history.replaceState = function (state: any, unused: string, url?: string | URL | null) {
    const existingKey = window.history.state?.__asmm_key || generateKey();
    const existingIdx = window.history.state?.__asmm_idx ?? 0;

    const enhancedState = {
      ...(state && typeof state === 'object' ? state : {}),
      __asmm_key: existingKey,
      __asmm_idx: existingIdx
    };

    originalReplaceState(enhancedState, unused, url);
  };

  // 7. Popstate listener (handles browser Back and Forward buttons)
  window.addEventListener('popstate', (event) => {
    // If popstate was dispatched immediately after pushState, pushState already handled it
    if (Date.now() - lastPushTimestamp < 60) {
      return;
    }

    // Real browser Back / Forward action:
    const targetKey = event.state?.__asmm_key;
    const targetPath = window.location.pathname + window.location.search;
    restoreScrollFor(targetKey, targetPath);
  });

  // 8. Before unload / pagehide, save current position
  window.addEventListener('beforeunload', recordCurrentPosition);
  window.addEventListener('pagehide', recordCurrentPosition);
}
